import { SxProps, Theme } from '@mui/material';
import { DUTIES_COLOURS, DUTIES_RADIUS } from './theme';

// ============================================================================
// Dialog/Modal Styles
// ============================================================================

/**
 * Main modal dialog paper styling
 * Provides consistent modal appearance with rounded corners and responsive sizing
 */
export const modalPaperStyles: SxProps<Theme> = {
  borderRadius: DUTIES_RADIUS.md,
  maxWidth: '740px',
  maxHeight: { xs: '90vh', sm: '85vh' },
  display: 'flex',
  flexDirection: 'column',
  height: 'auto'
};

/**
 * Confirmation dialog paper styling
 * Smaller, simpler dialog for confirmations
 */
export const confirmationDialogPaperStyles: SxProps<Theme> = {
  borderRadius: DUTIES_RADIUS.md,
  padding: '8px'
};

/**
 * Modal header container styling
 */
export const modalHeaderStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  backgroundColor: DUTIES_COLOURS.backgroundLight
};

/**
 * Modal title typography styling
 */
export const modalTitleStyles: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '24px',
  lineHeight: '100%',
  color: 'rgba(0, 0, 0, 0.87)'
};

/**
 * Modal content area styling
 */
export const modalContentStyles: SxProps<Theme> = {
  padding: '16px 24px 0',
  backgroundColor: DUTIES_COLOURS.backgroundLight,
  overflowY: 'auto',
  flex: '0 0 auto'
};

/**
 * Confirmation dialog content styling
 */
export const confirmationDialogContentStyles: SxProps<Theme> = {
  padding: '16px 24px'
};

/**
 * Modal actions (footer) styling
 */
export const modalActionsStyles: SxProps<Theme> = {
  padding: '16px 24px',
  gap: '8px',
  justifyContent: 'space-between',
  backgroundColor: DUTIES_COLOURS.backgroundLight,
  flexShrink: 0
};

/**
 * Confirmation dialog actions styling
 */
export const confirmationDialogActionsStyles: SxProps<Theme> = {
  padding: '8px 16px 16px'
};

/**
 * Button group container (for multiple action buttons)
 */
export const buttonGroupStyles: SxProps<Theme> = {
  display: 'flex',
  gap: '8px',
  justifyContent: 'flex-end'
};

// ============================================================================
// IconButton Styles
// ============================================================================

/**
 * Close button styling (modal header)
 */
export const closeIconButtonStyles: SxProps<Theme> = {
  color: 'rgba(0, 0, 0, 0.54)',
  padding: '8px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)'
  }
};

/**
 * Delete row icon button styling
 */
export const deleteRowIconButtonStyles: SxProps<Theme> = {
  color: 'rgba(0, 0, 0, 0.54)',
  padding: '8px',
  mt: '4px',
  '&:hover': {
    color: 'rgba(0, 0, 0, 0.87)',
    backgroundColor: DUTIES_COLOURS.backgroundLight
  }
};

// ============================================================================
// TextField Styles
// ============================================================================

/**
 * Common TextField input styling for duty configuration forms
 * Provides consistent appearance across time and number inputs
 */
export const textFieldInputStyles: SxProps<Theme> = {
  height: '36px',
  padding: '4px 12px',
  backgroundColor: DUTIES_COLOURS.background,
  borderRadius: DUTIES_RADIUS.sm,
  fontSize: '14px',
  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: DUTIES_COLOURS.border,
    borderWidth: '1px'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: DUTIES_COLOURS.border
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: DUTIES_COLOURS.primaryAction,
    borderWidth: '1px'
  },
  '& input': {
    padding: '0'
  }
};

// ============================================================================
// Typography Styles
// ============================================================================

/**
 * Table header typography styling
 * Used for column headers in the time periods table
 */
export const tableHeaderTypographyStyles: SxProps<Theme> = {
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '16px',
  color: '#222',
  textTransform: 'capitalize',
  letterSpacing: '0.5px'
};

// ============================================================================
// Box/Layout Styles
// ============================================================================

/**
 * Grid template columns for time periods table
 * Responsive layout for time inputs, personnel count, and delete button
 */
export const tableGridColumns = {
  xs: '1fr 1fr 80px 48px',
  sm: '1fr 1fr 100px 48px',
  md: '1fr 1fr 120px 48px'
};

/**
 * Divider styling for table rows
 */
export const tableDividerStyles: SxProps<Theme> = {
  borderBottom: '1px solid #DDD',
  mb: '0'
};

// ============================================================================
// Button Styles
// ============================================================================

/**
 * Base styling for text buttons (Cancel, Add Time Period)
 * Provides consistent appearance with outlined style
 */
export const textButtonStyles: SxProps<Theme> = {
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  color: '#0A0A0A',
  border: `1px solid ${DUTIES_COLOURS.border}`,
  borderRadius: DUTIES_RADIUS.sm,
  padding: '6px 16px',
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.04)'
  }
};

/**
 * Primary action button styling (Save button)
 */
export const primaryButtonStyles: SxProps<Theme> = {
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  minWidth: '64px',
  padding: '6px 16px',
  backgroundColor: DUTIES_COLOURS.primaryAction,
  boxShadow: 'none',
  borderRadius: DUTIES_RADIUS.sm,
  '&:hover': {
    backgroundColor: DUTIES_COLOURS.primaryActionHover,
    boxShadow: 'none'
  },
  '&:active': {
    boxShadow: 'none'
  }
};

/**
 * Destructive action button styling (Delete button)
 */
export const destructiveButtonStyles: SxProps<Theme> = {
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  borderRadius: DUTIES_RADIUS.sm,
  padding: '6px 16px',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none'
  }
};

